import type { ReactNode } from 'react';
import { CountBadge } from '../CountBadge';
import { useProfileSectionTitle } from './profileSectionTitle';

/**
 * One block inside a `<ProfileShell>` panel.
 *
 * **A panel does not repeat its own name.** `<ProfileNav>` on the left already says which
 * panel is open, and a heading at the top of the content saying it again is the same value in
 * two slots — the rule reference §8 sets and the one this component used to break on every
 * profile. So `title` is optional, and the panels in this product pass none: the nav is the
 * title.
 *
 * The strip renders only when something is in it — a count, or an actions slot holding a
 * `<Menu>` trigger or a button that acts on this section alone. Same rule as the profile
 * card's cog: a header with nothing in it is a band of empty space above the content.
 *
 * **The section STACKS its children with a gap, and that is load-bearing.** A panel that puts
 * a `<ProfileBanner>` above a table used to render the two flush against each other — the
 * banner's bottom edge sitting on the toolbar's top border, reading as one malformed control
 * rather than two. The gap lives here rather than at the call site for the reason every other
 * "the guard belongs in the component" rule exists: a caller cannot be relied on to remember
 * it, and the one that forgets is the one that ships.
 *
 * A section with a single child is unaffected — a gap needs two things to sit between.
 *
 * Content is whatever the section holds; the section itself imposes no card, so a
 * `<PreviewTable>` and a full-width table both sit correctly in it.
 */
export function ProfileSection({
  title: titleProp,
  count,
  description,
  actions,
  children,
  id,
}: {
  /**
   * Overrides the heading. **Almost always omitted** — a section takes its name from the
   * nav entry that opened it, through `ProfileSectionTitleContext`, so the rail and the
   * heading cannot disagree about what the reader is looking at.
   *
   * Pass it only where a panel holds more than one section and they need telling apart,
   * or to say a section is named something the nav does not call it.
   */
  title?: string;
  count?: number;
  /** One line under the title. Context for the whole section, not a field description. */
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  /*
    The nav says which panel is open, and for a long time that was the argument for the
    panel carrying no heading of its own. Two changes since undercut it: a nav GROUP now
    collapses over the active entry — so a reader with `201 File` shut sees the group
    named, not the section — and a panel of any length scrolls the rail out of view
    entirely. Product call, 17 Sep 2026: the panel states its own name.
  */
  const inherited = useProfileSectionTitle();
  const title = titleProp ?? inherited;

  return (
    <section id={id} className="min-w-0">
      {title || count !== undefined || actions ? (
        <div className="flex min-w-0 items-center gap-1.5 pb-3">
          {title ? (
            /*
              `text-sm` and GREY — a step under the header title rather than equal to it.

              It shipped at the header's own `text-base font-medium text-slate-800` and was
              two things at once: the record name and the section name, identical in size,
              weight and tone, a band apart. The heading is chrome naming the region, not a
              second identity for the record, so it recedes — which is the same call the
              tree's lane labels and the filter rail's group headings already take.

              Still `font-medium`, and still an `<h2>`: it is a heading, and the outline
              reads record → section → card whatever the tone.
            */
            <h2 className="min-w-0 truncate text-sm font-medium text-slate-500">{title}</h2>
          ) : null}

          {count !== undefined ? <CountBadge count={count} tone="slate" /> : null}

          {actions ? <div className="ml-auto flex shrink-0 items-center">{actions}</div> : null}
        </div>
      ) : null}

      {description ? <p className="pb-2 text-xs text-slate-500">{description}</p> : null}

      {/*
        `gap-4` — the same rhythm a modal body spaces its fields at, so a banner over a table
        reads as two things in one section rather than one thing that has come apart.
      */}
      <div className="flex min-w-0 flex-col gap-4">{children}</div>
    </section>
  );
}
